import {
  CatalogByCategory,
  DATA_RELATION,
  ExtraCategory,
  MainCategoriesEnum,
  ProductConfig,
  ProductNameEnum,
  TextureCategory,
} from "@shared/core";
import packageJson from "../../package.json";
import { getInitialItemForCategory } from "../utils/common";
import type { ProductStateSlice } from "./index";

export const VERSION = packageJson.version;
export const STORAGE_PREFIX = `configuration-v${VERSION}-`;
export const keyFor = (p: ProductNameEnum) => `${STORAGE_PREFIX}${p}`;
export const ROOT_KEY = `configuration-root-v${VERSION}`; // last active product/category (optional)

/**
 * Merge texture + extras relations into a catalog for a product.
 * This is your "source of truth" for what can be selected.
 */
export const getDefaultDataByProduct = (
  productName: ProductNameEnum,
): CatalogByCategory => {
  /* @TODO must be in the 'packages' project */
  // @ts-ignore – DATA_* are compatible with CatalogByCategory
  return {
    ...DATA_RELATION[productName],
  };
};

/**
 * Compute a default ProductConfig from a catalog.
 * Used when there is no saved config in localStorage.
 */
export function defaultConfigFromCatalog(
  catalog: CatalogByCategory,
): ProductConfig {
  const cfg: ProductConfig = {};

  for (const [catKey, catVal] of Object.entries(catalog) as [
    MainCategoriesEnum,
    TextureCategory | ExtraCategory,
  ][]) {
    if (!catVal || !catVal.items?.length) continue;

    const initialUid = getInitialItemForCategory(catVal);

    if (catVal.type === "texture") {
      cfg[catKey] = { kind: "single", uid: initialUid };
    } else {
      if (catKey === MainCategoriesEnum.PROPULSION) {
        cfg[catKey] = { kind: "single", uid: initialUid };
      } else {
        cfg[catKey] = { kind: "multi", uids: initialUid ? [initialUid] : [] };
      }
    }
  }

  return cfg;
}
/**
 * Load a whole product node (`{ config, ui }`) from localStorage.
 * If parsing or shape validation fails – returns null.
 */
export function loadProductNode(
  product: ProductNameEnum,
): ProductStateSlice | null {
  try {
    const raw = localStorage.getItem(keyFor(product));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as any;
    if (!parsed || typeof parsed !== "object") return null;

    if (!parsed.config && !parsed.ui) {
      const config = parsed as ProductConfig;
      return {
        config,
        ui: {
          product,
          category: MainCategoriesEnum.PROPULSION,
          selectedGroup: {} as Record<MainCategoriesEnum, string | null>,
          mode: "day",
          locale: "en",
          led: "off",
        },
      };
    }

    const cfg: ProductConfig = parsed.config ?? {};
    const uiRaw = parsed.ui ?? {};

    const ui = {
      product,
      category: uiRaw.category ?? MainCategoriesEnum.PROPULSION,
      selectedGroup:
        uiRaw.selectedGroup ??
        ({} as Record<MainCategoriesEnum, string | null>),
      mode: uiRaw?.mode ?? "day",
      locale: uiRaw?.locale ?? "en",
      led: uiRaw?.led ?? "off",
    };

    return { config: cfg, ui };
  } catch {
    return null;
  }
}

/**
 * Persist a whole product node (`{ config, ui }`) for a product.
 */
export function saveProductNode(
  product: ProductNameEnum,
  node: ProductStateSlice,
) {
  try {
    localStorage.setItem(keyFor(product), JSON.stringify(node));
  } catch {
    // ignore quota / private mode errors
  }
}
